<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { Search } from 'lucide-svelte';
  import { quotationsApi } from '$lib/api/quotations';
  import { productsApi } from '$lib/api/products';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { QUOTE_VERTICAL_META } from '$lib/types';

  // Rails' SearchController requires a 2-char minimum before it runs any
  // query at all (see .claude/packs/rails-port-spec.md §6) — matched here
  // client-side so a 1-char query never even hits the two endpoints.
  const MIN_QUERY_LENGTH = 2;
  const RESULT_LIMIT = 20;

  // Local input state seeds from the route's ?q= (deep-linkable/bookmarkable,
  // matching every other filter in this app — see route.svelte.ts) and
  // writes back via `replace` (not `push`) so typing doesn't spam history
  // the way PipelineBoard's local-only search never had to worry about.
  const initialQuery = route.current.tab === 'search' ? (route.current.q ?? '') : '';
  let input = $state(initialQuery);
  let debounced = $state(initialQuery);
  let debounceTimer: ReturnType<typeof setTimeout>;
  $effect(() => {
    const value = input;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounced = value;
      route.replace({ tab: 'search', q: value || undefined });
    }, 300);
  });

  const query = $derived(debounced.trim());
  const isValidQuery = $derived(query.length >= MIN_QUERY_LENGTH);

  const quotationsQuery = createQuery(() => ({
    queryKey: ['search-quotations', query],
    queryFn: () => quotationsApi.list({ q: query, per_page: RESULT_LIMIT }),
    enabled: isValidQuery
  }));

  const productsQuery = createQuery(() => ({
    queryKey: ['search-products', query],
    queryFn: () => productsApi.list({ q: query, per_page: RESULT_LIMIT }),
    enabled: isValidQuery && session.isAdmin
  }));

  const STATUS_LABEL: Record<string, string> = {
    draft: 'Draft',
    revised: 'Revised',
    submitted: 'Submitted',
    priced: 'In review',
    approved: 'Approved'
  };
  const STATUS_CHIP: Record<string, string> = {
    draft: 'chip-gray',
    revised: 'chip-danger',
    submitted: 'chip-warn',
    priced: 'chip-teal',
    approved: 'chip-lime'
  };
</script>

<div class="pq-scroll flex-1 overflow-y-auto px-4 py-4 lg:px-7 lg:py-5">
  <div class="mx-auto flex max-w-[860px] flex-col gap-5">
    <div class="relative flex items-center">
      <Search class="pointer-events-none absolute left-3.5 h-4 w-4 text-dark-gray" />
      <input
        value={input}
        oninput={(e) => (input = (e.currentTarget as HTMLInputElement).value)}
        placeholder="Search quotations by client, project or number&hellip;"
        class="input w-full !py-3 !pl-10 !text-[14px]"
      />
    </div>

    {#if !isValidQuery}
      <p class="tym-loading-state">
        {query.length === 0 ? 'Start typing to search.' : `Enter at least ${MIN_QUERY_LENGTH} characters to search.`}
      </p>
    {:else}
      <div class="tym-card p-0">
        <div class="border-b border-toyama-cloudy-gray px-[18px] py-3.5">
          <h2 class="font-heading text-[14.5px] font-bold text-navy">Quotations</h2>
        </div>
        {#if quotationsQuery.isLoading}
          <p class="tym-loading-state">Searching&hellip;</p>
        {:else if quotationsQuery.isError}
          <p class="tym-error-state m-4">{getErrorMessage(quotationsQuery.error)}</p>
        {:else}
          <div class="tym-table-wrap tym-table-scroll">
            <table class="tym-table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Client</th>
                  <th>Project</th>
                  <th>Vertical</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {#each quotationsQuery.data?.results ?? [] as q (q.id)}
                  <tr class="clickable" onclick={() => route.push({ tab: 'quotations', id: q.id })}>
                    <td class="font-mono text-[12.5px]">{q.quotation_number}</td>
                    <td class="font-medium text-navy">{q.client_name || 'Untitled client'}</td>
                    <td class="muted">{q.project_name || 'Untitled project'}</td>
                    <td>
                      <span class="tym-badge text-toyama-teal bg-toyama-teal/10">
                        {QUOTE_VERTICAL_META[q.vertical]?.label ?? q.vertical}
                      </span>
                    </td>
                    <td>
                      <span class="chip {STATUS_CHIP[q.status] ?? 'chip-gray'}">{STATUS_LABEL[q.status] ?? q.status}</span>
                    </td>
                  </tr>
                {:else}
                  <tr><td class="empty" colspan="5">No quotations match "{query}".</td></tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>

      {#if session.isAdmin}
        <div class="tym-card p-0">
          <div class="border-b border-toyama-cloudy-gray px-[18px] py-3.5">
            <h2 class="font-heading text-[14.5px] font-bold text-navy">Products</h2>
          </div>
          {#if productsQuery.isLoading}
            <p class="tym-loading-state">Searching&hellip;</p>
          {:else if productsQuery.isError}
            <p class="tym-error-state m-4">{getErrorMessage(productsQuery.error)}</p>
          {:else}
            <div class="tym-table-wrap tym-table-scroll">
              <table class="tym-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Part number</th>
                    <th>Category</th>
                  </tr>
                </thead>
                <tbody>
                  {#each productsQuery.data?.results ?? [] as p (p.id)}
                    <tr>
                      <td class="font-medium text-navy">{p.name}</td>
                      <td class="font-mono text-[12.5px]">{p.part_number}</td>
                      <td class="muted">{p.category || '—'}</td>
                    </tr>
                  {:else}
                    <tr><td class="empty" colspan="3">No products match "{query}".</td></tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
</div>
